import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Bell, BellOff, X, Check, Clock, AlertTriangle, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const NotificationManager = () => {
  const { usuario } = useAuth();
  const [permission, setPermission] = useState('default');
  const [showPanel, setShowPanel] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Verificar permissão de notificação
  useEffect(() => {
    if ('Notification' in window) {
      setPermission(Notification.permission);
    }
  }, []);

  // Carregar eventos e verificar alertas
  const checkForAlerts = useCallback(async () => {
    if (!usuario) return;

    try {
      const [audienciasRes, processosRes, anotacoesRes] = await Promise.all([
        api.getAudiencias(),
        api.getProcessos(),
        api.getAnotacoes()
      ]);

      const agora = new Date();
      const em24h = new Date(agora.getTime() + 24 * 60 * 60 * 1000);
      const novosAlertas = [];

      // Audiências nas próximas 24h
      audienciasRes.data.forEach(aud => {
        if (aud.data_audiencia) {
          const dataAud = new Date(aud.data_audiencia);
          if (dataAud > agora && dataAud <= em24h) {
            novosAlertas.push({
              id: `aud-${aud.id}`,
              tipo: 'audiencia',
              titulo: 'Audiência próxima',
              mensagem: `${aud.tipo_audiencia} - ${dataAud.toLocaleDateString('pt-BR')} às ${dataAud.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`,
              data: dataAud,
              lido: false
            });
          }
        }
      });

      // Prazos nas próximas 24h
      processosRes.data.forEach(proc => {
        if (proc.data_limite) {
          const dataPrazo = new Date(proc.data_limite);
          if (dataPrazo > agora && dataPrazo <= em24h) {
            novosAlertas.push({
              id: `prazo-${proc.id}`,
              tipo: 'prazo',
              titulo: 'Prazo próximo',
              mensagem: `${proc.numero_processo} - ${dataPrazo.toLocaleDateString('pt-BR')}`,
              data: dataPrazo,
              lido: false
            });
          }
        }
      });

      // Anotações do dia
      anotacoesRes.data.forEach(anot => {
        if (anot.data_alerta && !anot.concluida) {
          const dataAnot = new Date(anot.data_alerta);
          if (dataAnot > agora && dataAnot <= em24h) {
            novosAlertas.push({
              id: `anot-${anot.id}`,
              tipo: 'anotacao',
              titulo: 'Lembrete',
              mensagem: `${anot.titulo} - ${dataAnot.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`,
              data: dataAnot,
              lido: false
            });
          }
        }
      });

      // Ordenar por data
      novosAlertas.sort((a, b) => a.data - b.data);

      // Verificar se há novos alertas para enviar notificação
      const alertasStorage = JSON.parse(localStorage.getItem('alertasNotificados') || '[]');
      
      novosAlertas.forEach(alerta => {
        if (!alertasStorage.includes(alerta.id) && permission === 'granted') {
          sendBrowserNotification(alerta);
          alertasStorage.push(alerta.id);
        }
      });

      localStorage.setItem('alertasNotificados', JSON.stringify(alertasStorage));
      setNotifications(novosAlertas);
      setUnreadCount(novosAlertas.filter(n => !n.lido).length);

    } catch (error) {
      console.error('Erro ao verificar alertas:', error);
    }
  }, [usuario, permission]);

  // Verificar alertas a cada 5 minutos
  useEffect(() => {
    checkForAlerts();
    const interval = setInterval(checkForAlerts, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [checkForAlerts]);

  // Solicitar permissão
  const requestPermission = async () => {
    if ('Notification' in window) {
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result === 'granted') {
        toast.success('Notificações ativadas!');
        // Enviar notificação de teste
        new Notification('Advocacia Fulini', {
          body: 'Notificações ativadas com sucesso!',
          icon: '/logo192.png'
        });
      } else {
        toast.error('Permissão de notificação negada');
      }
    }
  };

  // Enviar notificação do navegador
  const sendBrowserNotification = (alerta) => {
    if (permission === 'granted') {
      new Notification('Advocacia Fulini', {
        body: `${alerta.titulo}: ${alerta.mensagem}`,
        icon: '/logo192.png',
        tag: alerta.id
      });
    }
  };

  // Marcar como lido
  const markAsRead = (id) => {
    setNotifications(prev => 
      prev.map(n => n.id === id ? { ...n, lido: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  // Marcar todas como lidas
  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, lido: true })));
    setUnreadCount(0);
  };

  const getIcon = (tipo) => {
    switch (tipo) {
      case 'audiencia': return <Calendar className="w-4 h-4 text-purple-600" />;
      case 'prazo': return <AlertTriangle className="w-4 h-4 text-red-600" />;
      case 'anotacao': return <Bell className="w-4 h-4 text-blue-600" />;
      default: return <Bell className="w-4 h-4" />;
    }
  };

  if (!usuario) return null;

  return (
    <div className="relative">
      {/* Botão de notificações */}
      <Button
        variant="ghost"
        size="sm"
        className="relative"
        onClick={() => setShowPanel(!showPanel)}
        data-testid="notification-button"
      >
        {permission === 'granted' ? (
          <Bell className="w-5 h-5" />
        ) : (
          <BellOff className="w-5 h-5 text-slate-400" />
        )}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
            {unreadCount}
          </span>
        )}
      </Button>

      {/* Painel de notificações */}
      {showPanel && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-lg shadow-lg border z-50" data-testid="notification-panel">
          <div className="p-3 border-b flex justify-between items-center">
            <h3 className="font-semibold">Notificações</h3>
            <div className="flex gap-2">
              {unreadCount > 0 && (
                <Button variant="ghost" size="sm" onClick={markAllAsRead}>
                  <Check className="w-4 h-4 mr-1" />
                  Marcar lidas
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={() => setShowPanel(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Status das notificações */}
          {permission !== 'granted' && (
            <div className="p-3 bg-amber-50 border-b">
              <p className="text-sm text-amber-700 mb-2">
                Ative as notificações para receber alertas
              </p>
              <Button size="sm" onClick={requestPermission}>
                <Bell className="w-4 h-4 mr-1" />
                Ativar Notificações
              </Button>
            </div>
          )}

          {/* Lista de notificações */}
          <div className="max-h-80 overflow-y-auto">
            {notifications.length > 0 ? (
              notifications.map(notif => (
                <div
                  key={notif.id}
                  className={`p-3 border-b hover:bg-slate-50 cursor-pointer ${!notif.lido ? 'bg-blue-50' : ''}`}
                  onClick={() => markAsRead(notif.id)}
                >
                  <div className="flex gap-2">
                    {getIcon(notif.tipo)}
                    <div className="flex-1">
                      <p className="font-medium text-sm">{notif.titulo}</p>
                      <p className="text-xs text-slate-600">{notif.mensagem}</p>
                      <p className="text-xs text-slate-400 mt-1">
                        <Clock className="w-3 h-3 inline mr-1" />
                        {notif.data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    {!notif.lido && (
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="p-6 text-center text-slate-500">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">Nenhuma notificação</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationManager;
